import React, { useState, useEffect, Fragment } from 'react';
import { useBodyScrollLock } from '../../../../../hooks/useBodyScrollLock';
import {
    ModalHeader,
    ModalBody,
    Row,
    Col,
    FormGroup,
    Label,
    Table,
} from 'reactstrap';
import Rodal from '../../../../../components/Rodal/Rodal';
import createNotification from '../../../../../utils/createNotification';
import { useStateSelector } from '../../../../../store/selectors';
import {
    convertOwnersPracticeToBackend,
} from '../../../../Practices/practiceDataMapper';
import Helpers from '../../../../../utils/helper';
import Loader from 'react-loaders';
import { PRACTICE_OWNERSHIP_TYPES } from '../../../../../utils/constants/PracticeOwnerType.enum';
import { faClockRotateLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tooltip } from 'react-tooltip';
import ConfirmationModal from '../../../../../components/Modal/ConfirmationModal';
import PracticeAffiliationAndMembers from './PracticeAffiliationAndMembers';
import './UserDetailsModal.scss';
import { ExtendedPracticeData } from '../models/ExtendedPracticeData';

interface PracticeDetailsModalProps {
    isVisible?: boolean;
    onClose: () => void;
    practiceId: number;
    userId?: number;
    renderInline?: boolean;
}

const PracticeDetailsModal: React.FC<PracticeDetailsModalProps> = (props) => {
    const axios = useStateSelector((state) => state.core.axios);
    const [practiceData, setPracticeData] = useState<ExtendedPracticeData[]>(
        []
    );
    const [isLoading, setIsLoading] = useState(false);
    const [isConfirmationDialogVisible, setIsConfirmationDialogVisible] =
        useState(false);
    const [practiceIdToConvertToFormer, setPracticeIdToConvertToFormer] =
        useState<number | null>(null);

    useBodyScrollLock(!props.renderInline && (props.isVisible ?? false));

    useEffect(() => {
        const canFetch = props.renderInline
            ? !!props.practiceId
            : !!props.isVisible && !!props.practiceId;
        if (canFetch) {
            fetchPracticeDetails(props.practiceId, props.userId);
        }
    }, [props.isVisible, props.practiceId, props.userId, props.renderInline]);

    const fetchPracticeDetails = (practiceId: number, userId?: number) => {
        setIsLoading(true);

        if (userId) {
            const getUserPractices = axios.get(
                '/api/Practice/GetUserPractices',
                { params: { userId } }
            );
            const getUserDetails = axios.get(
                `/api/Users/GetUserDetails?userId=${userId}`
            );

            Promise.all([getUserPractices, getUserDetails])
                .then(([practicesRes, userDetailsRes]) => {
                    const list = practicesRes?.data?.data || [];
                    const summaryPractice = list.find(
                        (p: any) =>
                            p.id === practiceId || p.practiceID === practiceId
                    );

                    const adminPractices =
                        userDetailsRes?.data?.data?.practices || [];
                    const adminPractice = adminPractices.find(
                        (p: any) =>
                            (p.id ?? p.practiceID) ===
                            (summaryPractice?.id ??
                                summaryPractice?.practiceID ??
                                practiceId)
                    );

                    const baseInfo: any = adminPractice || summaryPractice;

                    if (!baseInfo) {
                        createNotification(
                            'No practice found for selected user',
                            'error'
                        );
                        return;
                    }

                    const ownersPracticeValue =
                        baseInfo.ownersPractice ?? baseInfo.OwnersPractice;
                    const normalizedOwnersPractice = ['1', '2', '0'].includes(
                        ownersPracticeValue
                    )
                        ? convertOwnersPracticeToBackend(ownersPracticeValue)
                        : ownersPracticeValue ?? 'no';
                    
                        const mappedPractice: ExtendedPracticeData = {
                        ...baseInfo,
                        id: baseInfo.id ?? baseInfo.practiceID ?? practiceId,
                        practiceID:
                            baseInfo.practiceID ?? baseInfo.id ?? practiceId,
                        principalOwnerPhone: Helpers.trimPhoneNumberFromMask(
                            baseInfo.principalOwnerPhone
                        ),
                        principalOwnerId:
                            baseInfo.principalOwnerId ??
                            baseInfo.users?.[0]?.id ??
                            0,
                        practiceOwnerEmail:
                            baseInfo.practiceOwnerEmail ?? baseInfo.email ?? '',
                        registrationDate:
                            baseInfo.registrationDate ??
                            baseInfo.ownershipStartDate ??
                            undefined,
                        groupAffiliation: baseInfo.groupAffiliation ?? {},
                        associateMembers: baseInfo.associateMembers ?? [],
                        reportsList: baseInfo.reportsList ?? [],
                        practiceFteDvm:
                            baseInfo.practiceFteDvm ?? baseInfo.noOfFteDvms,
                        ltmRevenue: baseInfo.ltmRevenue,
                        practiceFteDvmNumber: baseInfo.practiceFteDvmNumber,
                        ownersPractice: normalizedOwnersPractice,
                    };

                    setPracticeData([mappedPractice]);
                })
                .catch(() => {
                    createNotification(
                        'An error occurred while fetching practice info',
                        'error'
                    );
                })
                .finally(() => {
                    setIsLoading(false);
                });
        } else {
            axios
                .get(`/api/practice/GetPractice/${practiceId}`)
                .then((response) => {
                    if (response.data.isError) {
                        createNotification(
                            'An error occurred while fetching practice info',
                            'error'
                        );
                    } else {
                        const practiceInfo = response.data.data;
                        const ownersPracticeValue =
                            practiceInfo.ownersPractice ??
                            practiceInfo.OwnersPractice;
                        const normalizedOwnersPractice = [
                            '1',
                            '2',
                            '0',
                        ].includes(ownersPracticeValue)
                            ? convertOwnersPracticeToBackend(
                                  ownersPracticeValue
                              )
                            : ownersPracticeValue ?? 'no';
                            
                        const mappedPractice: ExtendedPracticeData = {
                            ...practiceInfo,
                            id: practiceInfo.id || practiceId,
                            principalOwnerPhone:
                                Helpers.trimPhoneNumberFromMask(
                                    practiceInfo.principalOwnerPhone
                                ),
                            practiceID: practiceInfo.id || practiceId,
                            principalOwnerId:
                                practiceInfo.principalOwnerId ?? 0,
                            practiceOwnerEmail:
                                practiceInfo.practiceOwnerEmail ??
                                practiceInfo.principalOwnereMail ??
                                practiceInfo.email ??
                                '',
                            registrationDate:
                                practiceInfo.registrationDate ??
                                practiceInfo.ownershipStartDate ??
                                undefined,
                            groupAffiliation:
                                practiceInfo.groupAffiliation ?? {},
                            associateMembers:
                                practiceInfo.associateMembers ?? [],
                            reportsList: practiceInfo.reportsList ?? [],
                            practiceFteDvm:
                                practiceInfo.practiceFteDvm ??
                                practiceInfo.noOfFteDvms ??
                                practiceInfo.doctors,
                            ltmRevenue: practiceInfo.ltmRevenue,
                            practiceFteDvmNumber:
                                practiceInfo.practiceFteDvmNumber,
                            ownersPractice: normalizedOwnersPractice,
                        };
                        setPracticeData([mappedPractice]);
                    }
                })
                .catch(() => {
                    createNotification(
                        'An error occurred while fetching practice info',
                        'error'
                    );
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    };

    const convertPracticeToFormer = () => {
        if (!practiceIdToConvertToFormer || practiceData.length === 0) return;

        const practice = practiceData[0];
        axios
            .put(
                `/api/Practice/ConvertToFormer/${practiceIdToConvertToFormer}/${
                    practice.principalOwnerId || props.userId || 0
                }`
            )
            .then(() => {
                fetchPracticeDetails(props.practiceId, props.userId);
                setIsConfirmationDialogVisible(false);
                setPracticeIdToConvertToFormer(null);
            })
            .catch(function (error) {
                createNotification(error, 'error');
            });
    };

    const reportList = (data: any[]) => {
        if (!data || data.length === 0) return null;

        return (
            <Table className="rep table-striped table-bordered">
                <thead>
                    <tr>
                        <th>ID</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Created On</th>
                    </tr>
                </thead>
                <tbody>
                    {data.map((val, index) => (
                        <tr key={index}>
                            <td>{val.reportID}</td>
                            <td>{val.reportType}</td>
                            <td>{val.status}</td>
                            <td>
                                {val.createdOn
                                    ? new Date(
                                          val.createdOn
                                      ).toLocaleDateString('en-US')
                                    : ''}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        );
    };

    const getPracticeStatus = (practice: ExtendedPracticeData) => {
        const isFormer =
            practice.practiceOwnershipType == PRACTICE_OWNERSHIP_TYPES.FORMER;
        const showConvertButton = props.userId != null && props.userId !== 0;
        return (
            <FormGroup>
                <Label for="practiceStatus">Practice Status</Label>
                <h6>
                    {isFormer ? (
                        <>Former Practice</>
                    ) : (
                        <>
                            Current Practice
                            {showConvertButton && (
                                <>
                                    {' '}
                                    <FontAwesomeIcon
                                        id={`info-${
                                            practice.id ||
                                            practice.practiceID ||
                                            props.practiceId
                                        }`}
                                        onClick={() => {
                                            setIsConfirmationDialogVisible(
                                                true
                                            );
                                            setPracticeIdToConvertToFormer(
                                                practice.id ||
                                                    practice.practiceID ||
                                                    props.practiceId
                                            );
                                        }}
                                        icon={faClockRotateLeft}
                                    />{' '}
                                    <Tooltip
                                        anchorId={`info-${
                                            practice.id ||
                                            practice.practiceID ||
                                            props.practiceId
                                        }`}
                                        className="tooltip-content"
                                        id="event"
                                        place="right">
                                        <div>Make Emeritus Practice</div>
                                    </Tooltip>
                                </>
                            )}
                        </>
                    )}
                </h6>
            </FormGroup>
        );
    };

    const getPracticeOwnerEmail = (practice: ExtendedPracticeData) => {
        return (
            <FormGroup>
                <Label for="practiceOwnerEmail">Practice Owner Email</Label>
                <h6>{practice.practiceOwnerEmail || 'NA'}</h6>
            </FormGroup>
        );
    };

    const getRegisteredDate = (practice: ExtendedPracticeData) => {
        return (
            <FormGroup>
                <Label for="registrationDate">Registered Date</Label>
                <h6>
                    {practice.registrationDate
                        ? new Date(practice.registrationDate).toLocaleString(
                              'en-US'
                          )
                        : 'NA'}
                </h6>
            </FormGroup>
        );
    };

    const practiseDetails = () => {
        if (practiceData.length === 0) return null;

        const practice = practiceData[0];
        return (
            <div className="practice-profile">
                {!props.renderInline && (
                    <Row>
                        <Col md={4}>{getPracticeStatus(practice)}</Col>
                        <Col md={4}>{getPracticeOwnerEmail(practice)}</Col>
                        <Col md={4}>{getRegisteredDate(practice)}</Col>
                    </Row>
                )}
                <PracticeAffiliationAndMembers practice={practice as any} />
                {practice.reportsList && practice.reportsList.length > 0
                    ? reportList(practice.reportsList)
                    : ''}
            </div>
        );
    };

    const renderLoader = () => {
        return (
            <div
                className="loader-container"
                style={{ width: '65vw', height: '10vw' }}>
                <div className="loader-container-inner">
                    <div className="text-center">
                        <Loader active={isLoading} type="ball-scale-multiple" />
                    </div>
                    <h6 className="mt-5">Loading practice data...</h6>
                </div>
            </div>
        );
    };

    const handleClose = () => {
        props.onClose();
    };

    return (
        <Fragment>
            {props.renderInline ? (
                <div className="practice-details-inline">
                    {isLoading ? (
                        renderLoader()
                    ) : practiceData.length > 0 ? (
                        practiseDetails()
                    ) : (
                        <div className="empty-msg">No Data found</div>
                    )}
                </div>
            ) : (
                <Rodal
                    width={1285}
                    height={1000}
                    visible={props.isVisible}
                    animation={'slideRight'}
                    showCloseButton={true}
                    className={'cls-practice-details-modal'}
                    onClose={handleClose}
                    showMask={false}>
                    <ModalHeader>Registered Practice</ModalHeader>
                    <ModalBody>
                        {isLoading ? (
                            renderLoader()
                        ) : practiceData.length > 0 ? (
                            practiseDetails()
                        ) : (
                            <div className="empty-msg">No Data found</div>
                        )}
                    </ModalBody>
                </Rodal>
            )}

            {isConfirmationDialogVisible && (
                <ConfirmationModal
                    header={`Confirmation`}
                    isVisible={isConfirmationDialogVisible}
                    setIsVisible={setIsConfirmationDialogVisible}
                    confirmationText={`Are you sure you want to make this practice Emeritus?`}
                    params={undefined}
                    onConfirm={() => {
                        convertPracticeToFormer();
                    }}
                    nextButtonText="Yes, I'm sure"
                    cancelButtonText="No, take me back"
                />
            )}
        </Fragment>
    );
};

export default PracticeDetailsModal;
